import React, { useState } from 'react';
import {
  PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Types
type ValuationComponent = {
  name: string;
  value: number;
  color: string;
};

type ComparableProperty = {
  id: number;
  location: string;
  size: number;
  value: number;
  date: string;
};

// Data from the valuation report
const valuationData: ValuationComponent[] = [
  { name: 'Main House', value: 48797250, color: '#4e79a7' },
  { name: 'Annex', value: 5000000, color: '#f28e2b' },
  { name: 'Fence', value: 4500000, color: '#e15759' },
  { name: 'Retaining Wall', value: 3600000, color: '#76b7b2' },
  { name: 'Entrance Gate', value: 756250, color: '#59a14f' },
  { name: 'Paved Area', value: 2340000, color: '#edc948' },
  { name: 'Septic Tank & Landscaping', value: 2000000, color: '#b07aa1' },
  { name: 'Land Value', value: 18975000, color: '#ff9da7' },
];

const valuationMethods = [
  { method: 'Sales Comparison', weight: 40, value: 34400000 },
  { method: 'Depreciated Replacement Cost', weight: 35, value: 30100000 },
  { method: 'Cost Approach', weight: 25, value: 21500000 },
];

const comparableProperties: ComparableProperty[] = [
  { id: 1, location: 'Rubungo', size: 350, value: 86000000, date: '2022-01-31' },
  { id: 2, location: 'Kinyinya', size: 400, value: 92000000, date: '2022-03-15' },
  { id: 3, location: 'Gacuriro', size: 300, value: 78000000, date: '2021-11-22' },
  { id: 4, location: 'Bumbogo', size: 380, value: 82000000, date: '2022-02-10' },
];

// Custom icon for map marker
const propertyIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/447/447031.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const PropertyValuationDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'breakdown' | 'comparison' | 'location'>('breakdown');
  const [selectedComparable, setSelectedComparable] = useState<number | null>(null);

  // Format currency for display
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('rw-RW', {
      style: 'currency',
      currency: 'RWF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate percentages for display
  const totalValue = valuationData.reduce((sum, item) => sum + item.value, 0);
  const valuationDataWithPercentage = valuationData.map(item => ({
    ...item,
    percentage: Math.round((item.value / totalValue) * 100),
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-800 text-white p-6 shadow-md">
        <div className="container mx-auto">
          <h1 className="text-3xl font-bold">Property Valuation Dashboard</h1>
          <p className="text-blue-200">Plot 3903, Rubungo Village, Kigali City</p>
          <div className="mt-4 flex items-center justify-between">
            <div>
              <p className="text-sm">Valuation Date: 31st January, 2022</p>
              <p className="text-sm">Client: GASHAGAZA LEON AND UWASE SONIA</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-semibold">Open Market Value</p>
              <p className="text-3xl font-bold">{formatCurrency(86000000)}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white shadow-sm">
        <div className="container mx-auto flex">
          <button
            className={`px-6 py-4 font-medium ${activeTab === 'breakdown' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('breakdown')}
          >
            Valuation Breakdown
          </button>
          <button
            className={`px-6 py-4 font-medium ${activeTab === 'comparison' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('comparison')}
          >
            Market Comparison
          </button>
          <button
            className={`px-6 py-4 font-medium ${activeTab === 'location' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
            onClick={() => setActiveTab('location')}
          >
            Location Analysis
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        {activeTab === 'breakdown' && (
          <div className="space-y-8">
            {/* Valuation Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Valuation Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600">Open Market Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(86000000)}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600">Insurance Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(67000000)}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-600">Forced Sale Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(60200000)}</p>
                </div>
              </div>
            </div>

            {/* Valuation Components */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Valuation Components</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={valuationDataWithPercentage}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percentage }) => `${name} ${percentage}%`}
                      >
                        {valuationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div className="space-y-4">
                    {valuationDataWithPercentage.map((item) => (
                      <div key={item.name} className="flex items-center">
                        <div
                          className="w-4 h-4 rounded-full mr-2"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <span className="font-medium">{item.name}</span>
                            <span className="font-semibold">{formatCurrency(item.value)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${item.percentage}%`,
                                backgroundColor: item.color,
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> The valuation was derived using multiple approaches:
                      Sales Comparison (40%), Depreciated Replacement Cost (35%), and Cost Approach (25%).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Valuation Methods */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Valuation Methods</h2>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    layout="vertical"
                    data={valuationMethods}
                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  >
                    <CartesianGrid stroke="#f5f5f5" />
                    <XAxis type="number" />
                    <YAxis dataKey="method" type="category" scale="band" />
                    <Tooltip formatter={(value) => [`${value}%`, 'Weight']} />
                    <Legend />
                    <Bar dataKey="weight" barSize={20} fill="#413ea0" name="Method Weight (%)" />
                    <Line dataKey="value" stroke="#ff7300" name="Value Contribution (RWF)" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Property Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-lg mb-2">Construction Details</h3>
                  <ul className="space-y-2">
                    <li className="flex justify-between">
                      <span className="text-gray-600">Roof:</span>
                      <span>Flat structure with versatile iron sheet</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-600">Walls:</span>
                      <span>Reinforced concrete with brick infill</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-600">Floor:</span>
                      <span>Ceramic tiles throughout</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-600">Doors & Windows:</span>
                      <span>Glazed steel casement</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-lg mb-2">Accommodation</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-blue-600">Bedrooms</p>
                      <p className="text-2xl font-bold text-center">4</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-sm text-green-600">Bathrooms</p>
                      <p className="text-2xl font-bold text-center">3</p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-sm text-purple-600">Living Areas</p>
                      <p className="text-2xl font-bold text-center">2</p>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <p className="text-sm text-yellow-600">Plot Size</p>
                      <p className="text-2xl font-bold text-center">342 m²</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'comparison' && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Market Comparison</h2>
              <div className="h-96 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={comparableProperties}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="location" angle={-45} textAnchor="end" height={60} />
                    <YAxis 
                      tickFormatter={(value) => `${value / 1000000}M`}
                      label={{ value: 'Value (RWF)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Legend />
                    <Bar 
                      dataKey="value" 
                      fill="#8884d8" 
                      name="Property Value"
                      onClick={(data) => setSelectedComparable(data.id)}
                    />
                    <Bar 
                      dataKey="size" 
                      fill="#82ca9d" 
                      name="Size (m²)"
                      onClick={(data) => setSelectedComparable(data.id)}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {selectedComparable && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-medium text-lg mb-2">
                    Selected Property: {comparableProperties.find(p => p.id === selectedComparable)?.location}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Value</p>
                      <p className="font-semibold">
                        {formatCurrency(comparableProperties.find(p => p.id === selectedComparable)?.value || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Size</p>
                      <p className="font-semibold">
                        {comparableProperties.find(p => p.id === selectedComparable)?.size} m²
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Valuation Date</p>
                      <p className="font-semibold">
                        {comparableProperties.find(p => p.id === selectedComparable)?.date}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Valuation Method Analysis</h2>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={valuationMethods}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="method" />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} />
                    <Radar
                      name="Weight (%)"
                      dataKey="weight"
                      stroke="#8884d8"
                      fill="#8884d8"
                      fillOpacity={0.6}
                    />
                    <Tooltip />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'location' && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Property Location</h2>
              <div className="h-96 rounded-lg overflow-hidden">
                <MapContainer
                  center={[-1.9242, 30.1488]}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker position={[-1.9242, 30.1488]} icon={propertyIcon}>
                    <Popup>
                      <div className="space-y-1">
                        <h3 className="font-bold">Plot 3903</h3>
                        <p>Rubungo Village, Kigali</p>
                        <p>Size: 342 m²</p>
                        <p>Value: {formatCurrency(86000000)}</p>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Zoning Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-lg mb-2">Current Zoning</h3>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-blue-800 font-semibold">R1B - Rural Residential Zone</p>
                    <p className="text-sm mt-2">
                      The R1B District is intended to offer low rise housing as part of the farming community
                      and complementary public facilities as needed. The purpose is to create a pleasant single
                      family area in the rural areas of the city.
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-lg mb-2">Services & Amenities</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Mains electricity connected</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Mains water connected</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Direct access to the property</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Access via tarmac road</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-sm">Valuation conducted by:</p>
              <p className="font-semibold">HOUSE OF PROFESSIONALS LTD</p>
              <p className="text-sm text-gray-400">Justice Avenue next to National Institute of Statistics of Rwanda (NISR)</p>
            </div>
            <div className="text-sm">
              <p>Certified Valuers:</p>
              <p>BYESHAMIKA Martin</p>
              <p>HAGUMAKUBAHO FABRICE</p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-700 text-center text-sm text-gray-400">
            <p>This report is valid only in original form without alterations. Valuation date: 31st January, 2022</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PropertyValuationDashboard;